import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, finalize, delay } from 'rxjs/operators';
import { LoadingService } from '../services/shared/loading/loading.service';

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
    private excludedUrls: string[] = ['/assets/', 'i18n', 'health-check', 'ping'];

    private excludedMethods: string[] = ['HEAD', 'OPTIONS'];

    constructor(private loadingService: LoadingService) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.shouldSkipLoading(req)) {
            return next.handle(req);
        }

        const loadingMessage = this.getLoadingMessage(req);
        this.loadingService.show(loadingMessage);

        return next.handle(req).pipe(
            tap(
                (event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse) {
                        // Request completed successfully
                    }
                },
                (error: any) => {
                    if (error instanceof HttpErrorResponse) {
                        // Request failed - loading will be hidden in finalize
                    }
                }
            ),
            finalize(() => {
                this.loadingService.hide();
            })
        );
    }

    private shouldSkipLoading(req: HttpRequest<any>): boolean {
        // Skip loading for certain URLs
        if (this.excludedUrls.some(excludedUrl => req.url.includes(excludedUrl))) {
            return true;
        }

        // Skip loading for certain HTTP methods
        if (this.excludedMethods.includes(req.method.toUpperCase())) {
            return true;
        }

        // Skip loading if request has a custom header to bypass loading
        if (req.headers.has('X-Skip-Loading')) {
            return true;
        }

        return false;
    }

    private getLoadingMessage(req: HttpRequest<any>): string {
        const customMessage = req.headers.get('X-Loading-Message');
        if (customMessage) {
            return customMessage;
        }

        // Generate message based on HTTP method and URL
        const method = req.method.toUpperCase();
        switch (method) {
            case 'GET':
                return 'Loading data...';
            case 'POST':
                return 'Processing...';
            case 'PUT':
            case 'PATCH':
                return 'Updating...';
            case 'DELETE':
                return 'Deleting...';
            default:
                return 'Processing...';
        }
    }
}
